import { Form } from "antd";
import { FormContainer } from "./styles";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
  useMemo,
} from "react";
import { staticFields } from "@/util/common_variable";
import { StyledGridRow } from "@/common/styles";
import DynamicForm from "@/common/DynamicForm";
import { PERSON } from "./Personal";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { setBookings } from "@/slices/bookingsSlice";
import { debounce, hasCheckSecurity, isNonEmptyVariable, convertApiFields } from "@/util/common_function";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";

const ArrestBook = forwardRef(
  ({ person = PERSON.INMATE, arrestbookFormRef, startingPoint }) => {
    const dispatch = useDispatch();
    const { bookings, bookingsFieldsEnabled } = useSelector(
      (state) => state?.bookings
    );
    const [form] = Form.useForm();
    const [arrestForm, setArrestInfoForm] = useState(null);
    const isFirstTime = useRef(true);
    const fullSearch = useSelector(state => state.fullSearch);
    const [apiFields, setApiFields] = useState([]);

    const globalClientParam = useSelector(
      (state) => state.config.globalClientParam
    );

    useEffect(() => {
      if (!globalClientParam) return;

      const getFieldsFromExtraAPI = () => {
        api.get(API_ENDPOINTS.getBookingExtra)
  .then((response) => {
    let data = response?.data || [];

    const arrestExtraParams =
      globalClientParam?.filter(
        (p) =>
          p.paramName === "ArrestReportsExtra" &&
          p.paramValue === "ArrestInformation"
      ) || [];

    const paramContexts = arrestExtraParams.map(
      (p) => p.paramContext
    );

    const filteredData = data.filter((item) => {
      const columnName = item?.columnName;

      return (
        columnName?.endsWith("_U") &&
        paramContexts.includes(columnName)
      );
    });

    let converted = convertApiFields(
      filteredData,
      "ArrestReportsExtra"
    );

    setApiFields(converted);
  });
      };

      getFieldsFromExtraAPI();
    }, [globalClientParam]);

    const arrestExtraFields = useMemo(() => {
      return globalClientParam?.filter(
        (p) =>
          p.paramName === "ArrestReportsExtra" &&
          p.paramValue === "ArrestInformation"
      );
    }, [globalClientParam]);

    const dynamicFields = useMemo(() => {
      if (!apiFields?.length) return [];

      return apiFields.map((field) => {
        const baseConfig = staticFields?.arrest_book_info[person]?.find(
          (sf) => sf.columnName === field.columnName
        );

        return {
          ...field,

          // fallback only if API didn't provide
          label: field.label || baseConfig?.label || field.columnName,
          type: field.type || baseConfig?.type || "varchar",
          refListName:
            field.refListName || baseConfig?.refListName || "",
          showSearch:
            field.showSearch ?? baseConfig?.showSearch ?? false,
        };
      });
    }, [apiFields, person]);

    useEffect(() => {
      if (bookings != null) {
        const ar = bookings?.arrestReports?.[0];
        const extraInitialValues = {};

        apiFields?.forEach((field) => {
          const key = field.columnName;

          const value = bookings?.arrestReports?.[0]?.arrestReportsExtra?.[0]?.[key];

          extraInitialValues[key] =
            value && key.toLowerCase().includes("date")
              ? (dayjs(value).isValid() ? dayjs(value) : null)
              : value;
        });

        setArrestInfoForm(() => ({
          ...ar,
          ...extraInitialValues, // ✅ MUST include this

          incarcerationDateTime: isNonEmptyVariable(ar?.incarcerationDateTime)
            ? dayjs(ar?.incarcerationDateTime)
            : null,

          arrestDateTime: bookings?.arrestReports?.[0]?.arrestDate
            ? dayjs(
                (isNonEmptyVariable(bookings?.arrestReports?.[0]?.arrestDate)
                  ? bookings?.arrestReports?.[0]?.arrestDate
                  : "") +
                " " +
                (isNonEmptyVariable(bookings?.arrestReports?.[0]?.arrestTime)
                  ? bookings?.arrestReports?.[0]?.arrestTime
                  : "")
              )
            : fullSearch ? "" : dayjs(),

          inmateTypeId:
            isNonEmptyVariable(bookings?.arrestReports?.[0]?.inmateTypeId)
              ? bookings?.arrestReports?.[0]?.inmateTypeId
              : null,
        }));
      } else {
        setArrestInfoForm(null);
      }
    }, [bookings]);

    // useEffect(() => {
    //   if (arrestForm && isFirstTime.current) {
    //     if (arrestForm !== null) {
    //       form.setFieldsValue(arrestForm);
    //     } else {
    //       form.resetFields();
    //     }
    //     isFirstTime.current = false
    //   }
    // }, [arrestForm]);

    useEffect(() => {
      if (arrestForm !== null) {
        form.setFieldsValue(arrestForm);
        // console.log("✅ form.setFieldsValue called with:", arrestForm);
      } else {
        form.resetFields();
        // console.log("Form reset (arrestForm is null)");
      }
    }, [arrestForm]);


    // Expose form methods to parent
    useImperativeHandle(arrestbookFormRef, () => ({
      submitForm: () => form.submit(),
      resetForm: () => form.resetFields(),
      getFormValues: () => form.getFieldsValue(),
      getFieldsError: () => form.getFieldsError(),
      isFieldsValidating: () => form.isFieldsValidating(),
      validateFields: () => form.validateFields(),
    }));

    const updateForm = (data) => {
      const extraFieldsData = {};

      apiFields?.forEach((field) => {
        const key = field.columnName;
        extraFieldsData[key] = data?.[key] || null;
      });

      dispatch(
        setBookings({
          arrestReports: [
            {
              ...bookings?.arrestReports?.[0],
              ...data,
              arrestReportsExtra: [
                {
                  ...bookings?.arrestReports?.[0]?.arrestReportsExtra?.[0],
                  ...extraFieldsData,
                },
              ],
            },
          ],
        })
      );
    };

    // const updateForm = (data) => {
    //   dispatch(
    //     setBookings({
    //       arrestReports: [
    //         {
    //           ...bookings?.arrestReports?.[0],
    //           ...data,

    //           arrestReportsExtra: [
    //             {
    //               ...bookings?.arrestReports?.[0]?.arrestReportsExtra?.[0],
    //               towingCompany: data?.towingCompany || null,
    //               intakeWarCheckDate: data?.intakeWarCheckDate || null,
    //             },
    //           ],
    //         },
    //       ],
    //     })
    //   );
    // };

    const dataModify = debounce(() => {
      const values = form.getFieldsValue();

      const data = {
        ...values,
        arrestDate: values?.arrestDateTime
          ? dayjs(values?.arrestDateTime).format("MM/DD/YYYY")
          : null,
        arrestTime: values?.arrestDateTime
          ? dayjs(values?.arrestDateTime).format("HH:mm")
          : null,
      };

      // Handle dynamic arrestReportsExtra fields
      apiFields?.forEach((field) => {
        const key = field.columnName;

        // find field config from staticFields
        const fieldConfig = staticFields?.arrest_book_info[person]?.find(
          (sf) => sf.columnName === key
        );

        if (values?.[key]) {
          if (fieldConfig?.type === "date") {
            data[key] = dayjs(values[key]).format("MM/DD/YYYY");
          } else if (fieldConfig?.type === "datetime") {
            data[key] = dayjs(values[key]).format("MM/DD/YYYY HH:mm");
          } else {
            data[key] = values[key];
          }
        } else {
          data[key] = null; // important for clearing values
        }
      });

      delete data?.arrestDateTime;

      updateForm(data);
    }, 800);

    const existingColumns = new Set(
      staticFields?.arrest_book_info[person]?.map((sf) => sf.columnName)
    );

    const filteredDynamicFields = dynamicFields?.filter(
      (df) => !existingColumns.has(df.columnName)
    );

    const finalFields = [
      ...staticFields?.arrest_book_info[person],
      ...filteredDynamicFields,
    ];

    return (
      <FormContainer>
        <Form
          form={form}
          initialValues={arrestForm}
          disabled={!bookingsFieldsEnabled}
          onFieldsChange={() => {
            dataModify();
          }}
        >
          <StyledGridRow gutter={[16]}>
            {finalFields?.map((sf, index) => {
              let view = hasCheckSecurity("Functionality", "field", "VIEW", "", "", "Bio", sf.columnName, startingPoint)
              let edit = hasCheckSecurity("Functionality", "field", "MODIFY", "", "", "Bio", sf.columnName, startingPoint)
              return (
                <DynamicForm
                  formAttributes={sf}
                  person={person}
                  key={index}
                  form={form}
                  bookingsFieldsEnabled={bookingsFieldsEnabled}
                  view={view}
                  edit={edit}
                  fullSearch={fullSearch}
                />
              );
            })}
          </StyledGridRow>
        </Form>
      </FormContainer>
    );
  }
);

export default ArrestBook;
